/**
 * 訪談時間軸(REQ-20260418015 改造;REQ-20260525001 廠商也支援)。
 * 新增 / 編輯改走 InterviewModal,不再 inline 展開 form。
 * 列表本身保留在操作台下方,點某筆的「編輯」→ Modal 開啟。
 *
 * ownerType 切換:
 *   - 'customer' → /customers/:id/interviews,客戶有 SALES owner 概念
 *   - 'supplier' → /suppliers/:id/interviews,廠商無 owner(全公司共用)
 */
import { useEffect, useRef, useState } from 'react';
import type { InterviewResponse, OrderShipmentStatus } from '@app/shared';
import {
  useInterviews,
  useInterviewsTotalAll,
  useInterviewTypes,
  useToggleInterviewNotify,
  type InterviewOwnerType,
} from './api';
import { InterviewModal } from './InterviewModal';
import { CreateReceivableModal } from './CreateReceivableModal';
import { InterviewImageWall } from './InterviewImageWall';
import { useAuthStore } from '../auth/store';
import { AttachmentList } from '../attachments/AttachmentList';
import { AttachmentUploader } from '../attachments/AttachmentUploader';

interface Props {
  /** 客戶 id 或廠商 id(由 ownerType 決定) */
  ownerId: number;
  ownerType: InterviewOwnerType;
  /** customer 模式才需要:客戶的 ownerId,用於 SALES 角色寫權限判斷。supplier 模式無視。 */
  customerOwnerId?: number | null;
  /** REQ-20260419010:從附件搜尋跳過來時,要 scroll + 高亮的訪談 id。 */
  highlightInterviewId?: number | undefined;
  /** REQ-20260421008 §2:新增訪談時自動帶入的預設聯絡人(customer.contact 或 supplier.boss/contact1)。 */
  defaultContact?: string | null;
  /** 高亮完成後通知父層清掉 URL 的 interviewId,避免重進時重複滾動。 */
  onHighlightDone?: () => void;
}

export function InterviewTimeline({
  ownerId,
  ownerType,
  customerOwnerId,
  highlightInterviewId,
  defaultContact,
  onHighlightDone,
}: Props): JSX.Element {
  // REQ-20260421009 §2:類別篩選;切換 owner 時重置為 null
  const [typeFilter, setTypeFilter] = useState<number | null>(null);
  // REQ-20260803003:關鍵字搜尋。searchText 是輸入框內容,q 是 debounce 後真正送出的條件
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    setTypeFilter(null);
    setSearchOpen(false);
    setSearchText('');
    setQ('');
  }, [ownerId, ownerType]);
  useEffect(() => {
    const t = window.setTimeout(() => setQ(searchText.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchText]);

  const typesQuery = useInterviewTypes();
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useInterviews(ownerId, ownerType, {
    interviewTypeId: typeFilter,
    q: q || undefined,
  });
  const totalAllQuery = useInterviewsTotalAll(ownerId, ownerType);

  /**
   * REQ-20260812004:分頁重新變成可見時重抓訪談列表,「新增應收」的紅黑燈號
   * 才會反映別的分頁做過的事。
   *
   * 使用者從「+新增應收」彈窗點「前往修改」會在**新分頁**開出貨管理
   * (REQ-20260811001),在那邊作廢(REQ-20260812002)後 invalidate 的是那個分頁的
   * 快取 —— 兩個分頁各有自己的 QueryClient(`app/query-client.ts` 是模組層級單例),
   * 全域又刻意關掉 `refetchOnWindowFocus`,所以切回來燈號還是黑的。
   * REQ-20260809003 當時把這件事記成「靠重新整理」的取捨,這裡用
   * REQ-20260812003 在彈窗上用過的同一套做法補上。
   *
   * 只有客戶訪談掛監聽:廠商訪談沒有應收概念,燈號根本不 render,
   * 掛了只是每次切分頁多打一次 API。
   */
  useEffect(() => {
    if (ownerType !== 'customer') return;
    const handleVisible = (): void => {
      // visibilitychange 兩個方向都會觸發,切走的那一次不必重抓
      if (document.visibilityState !== 'visible') return;
      void refetch();
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  }, [ownerType, refetch]);

  const me = useAuthStore((s) => s.user);
  // 客戶:ADMIN 或 SALES 且擁有此客戶才能寫
  // 廠商:ADMIN 或 SALES 都能寫(無 owner 概念)
  const canWrite =
    me?.role === 'ADMIN' ||
    (me?.role === 'SALES' &&
      (ownerType === 'supplier' || customerOwnerId === me.id));

  const [modal, setModal] = useState<{
    mode: 'new' | 'edit';
    interviewId?: number;
    copyFrom?: InterviewResponse;
  } | null>(null);
  const [wallOpen, setWallOpen] = useState(false);
  const [pendingHighlight, setPendingHighlight] = useState<number | null>(null);
  const [receivableFor, setReceivableFor] = useState<InterviewResponse | null>(null);

  const interviews = data?.pages.flatMap((p) => p.data) ?? [];
  const total = data?.pages[0]?.meta.total ?? 0;

  const effectiveHighlight = pendingHighlight ?? highlightInterviewId ?? null;
  useEffect(() => {
    if (!effectiveHighlight) return;
    const found = interviews.some((iv) => iv.id === effectiveHighlight);
    if (!found) {
      if (hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
      return;
    }
    const el = document.getElementById(`interview-${effectiveHighlight}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const cls = ['ring-2', 'ring-amber-400', 'ring-offset-2'];
    el.classList.add(...cls);
    const t = window.setTimeout(() => el.classList.remove(...cls), 2500);
    setPendingHighlight(null);
    onHighlightDone?.();
    return () => window.clearTimeout(t);
  }, [
    effectiveHighlight,
    interviews.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    onHighlightDone,
  ]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <section data-testid="interview-timeline">
      <header className="mb-3 flex items-center justify-between min-h-[2.25rem]">
        <h2 className="text-lg font-semibold" data-testid="interview-count">
          訪談紀錄(
          {isLoading
            ? '…'
            : (typeFilter != null || q !== '') &&
                totalAllQuery.data != null &&
                totalAllQuery.data !== total
              ? `${total} / ${totalAllQuery.data}`
              : total}
          )
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={typeFilter ?? ''}
            onChange={(e) => setTypeFilter(e.target.value === '' ? null : Number(e.target.value))}
            className="border rounded px-2 py-1.5 text-sm"
            data-testid="interview-type-filter"
            aria-label="訪談類別篩選"
          >
            <option value="">全部類別</option>
            {typesQuery.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          {/* REQ-20260803003:關鍵字搜尋。點按鈕原地展開成輸入框,X 清空並收合 */}
          {searchOpen ? (
            <div className="flex items-center border rounded pr-1 focus-within:border-slate-400">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setQ(searchText.trim());
                }}
                placeholder="搜尋訪談內容、聯絡人"
                className="px-2 py-1.5 text-sm outline-none w-48 rounded"
                data-testid="interview-search-input"
                aria-label="關鍵字搜尋訪談"
                autoFocus
              />
              <button
                type="button"
                onClick={() => {
                  setSearchText('');
                  setQ('');
                  setSearchOpen(false);
                }}
                className="text-slate-500 hover:text-slate-800 px-1 text-sm"
                title="清除搜尋"
                aria-label="清除搜尋"
                data-testid="interview-search-clear-btn"
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-100"
              data-testid="interview-search-btn"
            >
              🔍 關鍵搜尋
            </button>
          )}
          {/* 圖片牆目前只支援客戶(supplier 圖片牆 API 暫未實作) */}
          {ownerType === 'customer' ? (
            <button
              type="button"
              onClick={() => setWallOpen(true)}
              className="border border-emerald-300 bg-emerald-50 text-emerald-800 rounded px-3 py-1.5 text-sm hover:bg-emerald-100"
              data-testid="image-wall-btn"
            >
              圖檔瀏覽
            </button>
          ) : null}
          {canWrite ? (
            <button
              type="button"
              onClick={() => setModal({ mode: 'new' })}
              className="bg-blue-600 text-white rounded px-3 py-1.5 text-sm hover:bg-blue-700"
              data-testid="new-interview-btn"
            >
              新增訪談
            </button>
          ) : null}
        </div>
      </header>

      {isLoading ? (
        <p className="text-slate-500">訪談載入中…</p>
      ) : isError ? (
        <p role="alert" className="text-red-600">訪談載入失敗</p>
      ) : interviews.length === 0 ? (
        <p className="text-slate-500">目前沒有訪談紀錄</p>
      ) : null}

      <ol className="grid gap-3">
        {interviews.map((iv) => (
          <InterviewItem
            key={iv.id}
            iv={iv}
            ownerId={ownerId}
            ownerType={ownerType}
            canWrite={canWrite}
            onEdit={() => setModal({ mode: 'edit', interviewId: iv.id })}
            onCopy={() => setModal({ mode: 'new', copyFrom: iv })}
            onCreateReceivable={() => setReceivableFor(iv)}
            keyword={q}
          />
        ))}
      </ol>

      {hasNextPage ? (
        <div
          ref={sentinelRef}
          data-testid="interview-load-more-sentinel"
          className="text-center text-slate-500 text-sm py-3"
        >
          {isFetchingNextPage ? '載入更多…' : '下滑載入更多'}
        </div>
      ) : null}

      {modal ? (
        <InterviewModal
          open={true}
          onClose={() => setModal(null)}
          ownerId={ownerId}
          ownerType={ownerType}
          mode={modal.mode}
          interview={
            modal.interviewId != null
              ? (interviews.find((iv) => iv.id === modal.interviewId) ?? null)
              : null
          }
          defaultContact={defaultContact ?? null}
          copyFrom={modal.copyFrom ?? null}
        />
      ) : null}

      {receivableFor && ownerType === 'customer' ? (
        <CreateReceivableModal
          open={true}
          onClose={() => setReceivableFor(null)}
          customerId={ownerId}
          interview={receivableFor}
        />
      ) : null}

      {ownerType === 'customer' ? (
        <InterviewImageWall
          open={wallOpen}
          onClose={() => setWallOpen(false)}
          customerId={ownerId}
          onJumpToInterview={(interviewId) => {
            setWallOpen(false);
            setPendingHighlight(interviewId);
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * 出貨狀態徽章的文案 / 說明(REQ-20260815008)。
 * 文字與實際狀態一致 —— REQ-20260815007 才剛把收款徽章的文案修回這條原則,
 * 新的徽章不要重蹈覆轍。
 */
const SHIPMENT_BADGE_TEXTS: Record<OrderShipmentStatus, string> = {
  NONE: '📦 待出貨',
  PARTIAL: '🚚 部分出貨',
  FULL: '📦 已出貨',
};

const SHIPMENT_BADGE_TITLES: Record<OrderShipmentStatus, string> = {
  NONE: '這張單的商品還沒有任何出貨紀錄',
  PARTIAL: '這張單的商品只出了一部分,還有未出貨數量',
  FULL: '這張單的商品已全部出貨',
};

function InterviewItem({
  iv,
  ownerId,
  ownerType,
  canWrite,
  onEdit,
  onCopy,
  onCreateReceivable,
  keyword,
}: {
  iv: InterviewResponse;
  ownerId: number;
  ownerType: InterviewOwnerType;
  canWrite: boolean;
  onEdit: () => void;
  onCopy: () => void;
  onCreateReceivable: () => void;
  /** REQ-20260803003:目前生效的搜尋關鍵字,用於命中字串標紅;空字串代表未搜尋 */
  keyword: string;
}): JSX.Element {
  const toggleNotify = useToggleInterviewNotify(ownerId, ownerType);
  const notifyOn = !!iv.notifyDaily;
  // REQ-20260809003 §1:只有「進稿」要紅黑燈號 —— 紅色代表這筆進稿還沒建應收單。
  // 其他類別維持原樣式(仍可建應收,只是不做提醒)。
  const isSubmission = iv.interviewType?.code === 'SUBMISSION';
  const needsReceivable = isSubmission && !iv.hasReceivable;
  // REQ-20260814004:已建立(未作廢)的出貨單單號,直接顯示在訪談上。
  // 廠商訪談沒有應收概念,API 也不回這個欄位(與 hasReceivable 同),所以先擋掉。
  const receivableOrders = ownerType === 'customer' ? (iv.receivableOrders ?? []) : [];
  return (
    <li
      id={`interview-${iv.id}`}
      data-testid={`interview-item-${iv.id}`}
      className="bg-white border border-slate-200 rounded-md p-3 transition-colors duration-150 hover:border-emerald-400 hover:shadow-sm"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm text-slate-600 flex items-center gap-2 flex-wrap min-w-0">
          {/*
            REQ-20260810002:業務私帳徽章,放在整列**最前面**讓人一眼看出這筆不是公帳。
            紫色是這個畫面還沒用過的色系(類別是 slate、提醒是 amber、
            未建應收是 red、圖檔瀏覽是 emerald),不跟既有徽章撞色。
          */}
          {iv.isPrivateAccount ? (
            <span
              className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-800 border border-purple-300"
              title="帳款對象是個人,不列入公司月結對帳單"
              data-testid={`interview-private-badge-${iv.id}`}
            >
              👤 私帳
            </span>
          ) : null}
          <span className="inline-block px-2 py-0.5 rounded bg-slate-100">
            {iv.interviewType?.name ?? '未分類'}
          </span>
          <span>{iv.visitedAt ? formatDate(iv.visitedAt) : '未指定日期'}</span>
          <span className="text-slate-400">by {iv.createdBy.displayName}</span>
          {iv.contact ? (
            <>
              <span className="text-slate-300" aria-hidden="true">·</span>
              <span>聯絡人:{highlight(iv.contact, keyword)}</span>
            </>
          ) : null}
          {/*
            REQ-20260814004:這筆訪談已建立的出貨單單號。原生 <a> + 新分頁的開法沿用
            REQ-20260811001 的「前往修改」(react-router 的 <Link> 在此無作用,
            而且這支測試沒包 Router)。藍色是這一列還沒用過的色系
            (私帳 purple、類別 slate、提醒 amber、未建應收 red)。
            沒有單就什麼都不印 —— 不顯示「無」之類的佔位字。

            **目的地在 REQ-20260824009 換過**:從編輯表單(`?editOrderId=`)改成
            列印預覽頁。使用者的理由是「沒有看過內容怎會知道是否需要列印」——
            點單號時最先要的是**看這張單長怎樣**,改單與列印都是看完之後的決定,
            所以那兩個入口現在都放在預覽頁的工具列上(「列印 / 另存 PDF」與「修改訂單」)。
            `data-testid` 刻意維持 `interview-order-no-*` 不變 —— 它標的是「單號徽章」
            這個位置,不是它連去哪裡。

            REQ-20260825008 起多帶一個 `?from=`:值是**這位客戶的詳細頁**。
            從這裡進去的人心裡的「回去」是回到這位客戶,不是會計的出貨管理清單
            (`?from=` 原本只用來記住清單的篩選條件,見 REQ-20260820006)。
            這個值會一路傳到預覽頁的「修改訂單」與出貨管理的「儲存修改」,
            所以存完檔會回到這位客戶的頁面。
          */}
          {receivableOrders.map((o) => (
            <span key={o.id} className="inline-flex items-center gap-1">
              <a
                href={`/sales-orders/${o.id}/print?from=${encodeURIComponent(
                  `/customers/console?id=${ownerId}`,
                )}`}
                target="_blank"
                rel="noreferrer"
                className="inline-block px-2 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                title="開啟這張出貨單的列印預覽(可從裡面進入修改)"
                data-testid={`interview-order-no-${o.id}`}
              >
                {o.orderNo}
              </a>
              {/*
                REQ-20260824008 曾在這裡並排第二顆「🖨️ 列印」徽章(單號進編輯、列印另開一顆)。
                **REQ-20260824009 整顆移除**:單號徽章本身已經連到列印頁,兩顆按鈕的目的地
                完全相同,留著只是同一件事給兩個入口。這是對當初設計的**修正**,不是漏刪 ——
                當時的取捨是「不要動已經用了一週的單號徽章行為」,而使用者這次直接
                要求改掉單號徽章的目的地。
              */}
              {/*
                REQ-20260815001:同一張單的第二個入口 —— 直接進應收沖帳,
                客戶與這張單都由網址帶過去先備好,不用再打一次客戶編號。
                teal 是這一列還沒用過的色系(單號徽章自己是 blue),
                兩顆並排時一眼分得出誰是誰。

                REQ-20260815005:兩種收款狀態要一眼分得出來,所以一邊實心綠、一邊淺色。
                刻意不用淺綠 —— teal-50 與 green-50 太接近,並排時等於沒有區別;
                實心 + 換圖示連色弱也分得出來。emerald 是圖檔按鈕的色系,所以用
                green-600/700 這一階。連結目的地兩種狀態相同(REQ-20260815001 已驗過:
                對收完款的單開登錄收款,彈窗照常開、清單裡沒有那張單、不報錯)。

                REQ-20260815006:使用者要求把兩款呈現方式**整組對調** ——
                `settled=true`(已收齊)換成淺 teal + 💰、`settled=false`
                (還有未收餘額)換成實心綠 + ✅,當時連文案與 title 也一起搬過去。
                `settled` 的語意與計算完全沒變,只有畫面上的呈現互換位置。
                (**校正**:顏色 / 圖示的分配至今仍是這樣,但這段原本寫的
                「teal 是『💰 收款』、實心綠是『✅ 已收款』」已被下段推翻。)

                REQ-20260815007:顏色 / 圖示的分配沿用 REQ-20260815006 沒有再變,
                這次只把**文字**改回與實際收款狀態相符 —— `settled=true`
                (已收齊)顯示「已收款」、`settled=false`(還有未收餘額)顯示「收款」,
                title 跟著文字走。所以現在是 `true` → 淺 teal 的「💰 已收款」、
                `false` → 實心綠的「✅ 收款」,解掉 REQ-20260815006 風險欄記的
                「文案與事實相反」:文字說的就是這張單的實際狀態,深淺配色則維持
                使用者選定的呈現。
              */}
              <a
                href={`/accounting?tab=receivable&customerId=${ownerId}&orderNo=${encodeURIComponent(o.orderNo)}`}
                target="_blank"
                rel="noreferrer"
                className={`inline-block px-2 py-0.5 rounded border ${
                  o.settled
                    ? 'border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100'
                    : 'border-green-600 bg-green-600 text-white hover:bg-green-700'
                }`}
                title={
                  o.settled
                    ? '這張單款項已全額收訖,點開可查看收款登錄'
                    : '開啟這張單的收款登錄'
                }
                data-testid={`interview-receipt-link-${o.id}`}
              >
                {o.settled ? '💰 已收款' : '✅ 收款'}
              </a>
              {/*
                REQ-20260815008:出貨狀態徽章,排在收款徽章右邊 —— 兩個問題並排:
                錢收完了沒 / 貨出完了沒。呈現邏輯比照收款徽章:
                「完成」的那一端用淺色(已出貨 = 淺 slate,對照已收款的淺 teal),
                「還沒完成」的用實心強調色(未出貨 / 部分出貨 = 實心 amber,
                對照收款的實心綠),深淺與圖示一起變,色弱也分得出來。
                amber 是這一列還沒用過的色系(私帳 purple、單號 blue、收款 teal/green)。

                REQ-20260815009:原本這顆刻意做成單純顯示、不可點(理由是「出貨登記在
                出貨管理列表,訪談這一列只回答出完了沒」)。使用者要求反過來 ——
                看到「未出貨」的下一個動作幾乎必然就是去登記出貨,而照舊得自己切到會計、
                找到那張單、再按「出貨」。現在改成比照左邊的收款徽章直接深連結過去,
                開的是那張單的出貨登記彈窗(不是編輯表單)。

                REQ-20260825004:`FULL`(已出貨)原本維持 `<span>`,理由是「貨都出完了,
                點進去只會看到一個沒有東西可勾的彈窗」。REQ-20260825003 讓彈窗下方的
                歷次出貨紀錄可以修正出貨日期之後,那個理由不再成立 —— 出完貨的單點進去
                看得到歷次紀錄、也改得動打錯的日期,跟收款徽章「收完款也點得進去看紀錄」
                變成同一回事。所以三種狀態都做成連結,只有配色與 title 分開:
                完成用淺色(不是待辦),title 也不再寫「點開可登記出貨」——
                出完貨的單事實上不能再登記新的出貨。

                REQ-20260824008:徽章文字後面接上最近一次出貨的日期 —— 訪談頁看得到
                定稿下單日,卻看不出貨是哪天出的。日期是後端即時查 shipments 的
                `max(shippedAt)`(`lastShippedAt`),沒出過貨就是 null,自然不多印東西。
                title 維持原本的狀態說明,不重複講日期。

                REQ-20260904001:網址尾端多帶一個 `from` = 這位客戶的詳細頁,寫法與上面的
                單號徽章完全相同。從這裡進去登記出貨的人,送出成功(或關掉彈窗)之後要回的是
                **這位客戶**,不是儀表板 —— 儀表板是 REQ-20260826002 為了「從首頁未出貨清單
                一筆一筆處理」那條動線挑的落點,那個入口不帶 `from`,行為完全不變。
                怎麼解讀、跳去哪裡的守門都在 `SalesOrdersPanel`,這裡只負責把來源交出去。

                REQ-20260929003:**草稿單不畫出貨徽章**,改畫一顆「📝 草稿待過帳」。
                草稿沒有出貨紀錄,`shipmentStatus` 算出來是 NONE,原本會亮「📦 待出貨」並連到
                出貨登記彈窗 —— 但登記出貨只放行 POSTED(REQ-20260815008),點進去只看到
                「只有已過帳的出貨單可以登記出貨」、什麼都不能填,違反 REQ-20260815007
                「徽章文案必須與實際狀態相符」。「作廢並複製新單」一律產生草稿
                (REQ-20260821001),所以改過的單最常撞到。草稿的下一步是過帳,不是出貨,
                所以連到出貨管理清單並用 `q=單號` 篩出這張單(那一列有「過帳」鈕),
                `from` 寫法同上。實心 slate:灰色是「還沒生效」的慣用色,實心代表待辦,
                與已出貨的淺 slate 深淺相反、又不會跟 amber 的待出貨混淆;
                刻意不用 sky,它跟左邊的藍色單號徽章太接近。
              */}
              {o.isDraft ? (
                <a
                  href={`/accounting?tab=sales&q=${encodeURIComponent(
                    o.orderNo,
                  )}&from=${encodeURIComponent(`/customers/console?id=${ownerId}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block px-2 py-0.5 rounded border border-slate-500 bg-slate-500 text-white hover:bg-slate-600"
                  title="這張單還是草稿,要先在出貨管理按『過帳』才能登記出貨"
                  data-testid={`interview-order-draft-badge-${o.id}`}
                >
                  📝 草稿待過帳
                </a>
              ) : (
                <a
                  href={`/accounting?tab=sales&shipOrderId=${o.id}&orderNo=${encodeURIComponent(
                    o.orderNo,
                  )}&from=${encodeURIComponent(`/customers/console?id=${ownerId}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className={`inline-block px-2 py-0.5 rounded border ${
                    o.shipmentStatus === 'FULL'
                      ? 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                      : 'border-amber-500 bg-amber-500 text-white hover:bg-amber-600'
                  }`}
                  title={
                    o.shipmentStatus === 'FULL'
                      ? `${SHIPMENT_BADGE_TITLES[o.shipmentStatus]},點開可查看或修正出貨紀錄`
                      : `${SHIPMENT_BADGE_TITLES[o.shipmentStatus]},點開可登記出貨`
                  }
                  data-testid={`interview-shipment-badge-${o.id}`}
                >
                  {`${SHIPMENT_BADGE_TEXTS[o.shipmentStatus]}${
                    o.lastShippedAt ? ' ' + formatDate(o.lastShippedAt) : ''
                  }`}
                </a>
              )}
            </span>
          ))}
        </div>
        {canWrite ? (
          <div className="flex gap-1 shrink-0 items-center">
            <button
              type="button"
              onClick={() => toggleNotify.mutate({ id: iv.id, notifyDaily: !notifyOn })}
              disabled={toggleNotify.isPending}
              className={`text-sm border rounded px-2 py-0.5 disabled:opacity-50 ${
                notifyOn
                  ? 'bg-amber-100 border-amber-300 text-amber-800 hover:bg-amber-200'
                  : 'hover:bg-slate-100 text-slate-600'
              }`}
              title={notifyOn ? '已開啟每日追蹤提醒,點擊關閉' : '點擊開啟每日追蹤提醒'}
              aria-pressed={notifyOn}
              data-testid={`btn-toggle-notify-${iv.id}`}
            >
              {notifyOn ? '🔔 提醒中' : '🔕 提醒'}
            </button>
            <AttachmentUploader
              interviewId={iv.id}
              ownerId={ownerId}
              ownerType={ownerType}
              compact
            />
            <button
              type="button"
              onClick={onCopy}
              className="text-sm border rounded px-2 py-0.5 hover:bg-slate-100"
              title="複製此筆訪談的類別與 Notes,開新訪談"
              data-testid={`btn-copy-interview-${iv.id}`}
            >
              複製
            </button>
            {ownerType === 'customer' ? (
              <button
                type="button"
                onClick={onCreateReceivable}
                className={`text-sm border rounded px-2 py-0.5 ${
                  needsReceivable
                    ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                    : 'hover:bg-slate-100'
                }`}
                title={`以此筆訪談建立應收(銷貨)單${
                  isSubmission ? (iv.hasReceivable ? '(已建立應收)' : '(尚未建立應收)') : ''
                }`}
                data-testid={`btn-create-receivable-${iv.id}`}
              >
                新增應收
              </button>
            ) : null}
            <button
              type="button"
              onClick={onEdit}
              className="text-sm border rounded px-2 py-0.5 hover:bg-slate-100"
            >
              編輯
            </button>
          </div>
        ) : null}
      </div>
      {iv.notes ? (
        <p className="text-slate-800 whitespace-pre-wrap mt-2 text-sm">
          {highlight(iv.notes, keyword)}
        </p>
      ) : null}
      {/* REQ-20260421006: 外層只做預覽/下載,刪除 / 上傳進編輯 Modal */}
      <AttachmentList
        attachments={iv.attachments}
        canWrite={false}
        ownerId={ownerId}
        ownerType={ownerType}
        interviewId={iv.id}
      />
    </li>
  );
}

/**
 * REQ-20260803003:把 text 裡命中 keyword 的片段標紅。
 * 用 split + capture group(奇數 index 即命中片段)切字串再組 React node,
 * 不走 dangerouslySetInnerHTML,避免訪談內容被當成 HTML 執行。
 */
function highlight(text: string, keyword: string): JSX.Element | string {
  if (!keyword) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(keyword)})`, 'gi'));
  if (parts.length === 1) return text;
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="bg-transparent text-red-600 font-medium">
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}
